<script setup lang="ts">
// Vue 및 라이브러리 임포트
import { ref, computed, onMounted, watch } from 'vue';
import { Plus } from 'lucide-vue-next';
// shadcn-vue 컴포넌트 임포트
import { Button } from '@/components/shadcnui/button';
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcnui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcnui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useModel } from '@/composable/useModel';
import { useChipset } from '@/composable/useChipset';
// 타입 임포트
import type { model, addmodel } from '@/types/types';


// 스토어 및 composable 초기화
const RSStore = useRSStore()
const UserStore = useUserStore();
const { onReadModelList, onAddModel, onUpdateModel, onDeleteModel, onPaginationModel } = useModel()
const { onReadChipsetList } = useChipset();

// 상태 관리
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const currentPage = ref(1);
const filterChipset = ref(0);
const totalpage = ref(1);
const isLoading = ref(true);
const settingMode = ref(1);
const formData = ref<model>({ id: 0, name: "", sysid: "", chipset_id: 0 });
const isFormInvalid = computed(() => {
  return !formData.value.name?.trim() || !formData.value.sysid?.trim() || !formData.value.chipset_id;
});


// 필터링 조건 변경 시 모델 리스트 업데이트
watch([filterChipset], async () => {
  if (!settingMode.value){
  currentPage.value = 1; // 페이지 초기화
  UserStore.modelfilter.page = currentPage.value;
  UserStore.modelfilter.chipset_id = filterChipset.value;
  isLoading.value = true;
  totalpage.value = await onPaginationModel(UserStore.itemPerPage) ?? 1;
  await onReadModelList(UserStore.modelfilter).finally(() => {
    isLoading.value = false;  
  })};
});

// 폼 데이터 초기화 함수
const formDataReset = () => {
  formData.value = { id: 0, name: "", sysid: "", chipset_id: 0 };
};

// 다이얼로그 핸들러
const openAddDialog = () => {
  isUpdateMode.value = false;
  formDataReset();
  isDialogOpen.value = true;
};
const openUpdateDialog = (model: model) => {
  if (!UserStore.modelEdit && !UserStore.modelDelete) {
    return;
  }
  isUpdateMode.value = true;
  formData.value = { ...model };
  isDialogOpen.value = true;
};
const cancelDialog = () => {
  isDialogOpen.value = false;
  formDataReset();
};

// ADD/UPDATE/DELETE API 호출 핸들러 (API 호출 후 데이터 갱신)
const handleAdd = async () => {
  const addmodel: addmodel = {
    name: formData.value.name,
    chipset_id: formData.value.chipset_id,
    sysid: formData.value.sysid,
  };
  UserStore.modelfilter.page = 1; // 페이지네이션을 첫 페이지로 리셋
  isLoading.value = true;
  await onAddModel(addmodel);
  totalpage.value = await onPaginationModel(UserStore.itemPerPage) ?? 1;
  await onReadModelList(UserStore.modelfilter).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleUpdate = async () => {
  const updatemodel: model = { ...formData.value };
  console.log("model update test", { ...updatemodel });
  isLoading.value = true;
  await onUpdateModel(updatemodel);
  await onReadModelList(UserStore.modelfilter).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleDelete = async () => {
  isLoading.value = true;
  await onDeleteModel(formData.value.id);
  await onReadModelList(UserStore.modelfilter).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};

// 페이지네이션 핸들러
const paginationHandler = async (page: number) => {
  if ( page < 1 ){
    page = 1;
  }
  else if ( page > totalpage.value ){
    page = totalpage.value;
  }
  currentPage.value = page;
  UserStore.modelfilter.page = page;
  isLoading.value = true;
  await onReadModelList(UserStore.modelfilter).finally(() => {
    isLoading.value = false;
  });
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(async () => {
  totalpage.value = await onPaginationModel(UserStore.itemPerPage) ?? 1;
  if (UserStore.modelfilter.chipset_id) {
    filterChipset.value = UserStore.modelfilter.chipset_id;
  }
  if (UserStore.modelfilter.page) {
    currentPage.value = UserStore.modelfilter.page;
  }
  await onReadChipsetList({ page: 0, size: 0 });
  await onReadModelList(UserStore.modelfilter).finally(() => {
    isLoading.value = false;
  });
  settingMode.value = 0;
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-2">
      <h1 class="text-3xl font-bold text-foreground">모델</h1>
          <div class="flex gap-4 mb-2">
      <div class="w-48">
        <Label class="mb-2 block">Chipset</Label>
        <Select v-model="filterChipset">
          <SelectTrigger>
            <SelectValue placeholder="전체" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem :value="0">전체</SelectItem>
            <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id">
              {{ chipset.name }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    <div class="flex items-end">
      <Button @click="openAddDialog" :disabled="!UserStore.modelAdd">
        <Plus class="mr-2 h-4 w-4" /> Add
      </Button>
      </div>
      </div>
    </div>

    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 hover:bg-muted/50">
            <TableHead class="font-semibold w-[300px] px-5">ID</TableHead>
            <TableHead class="font-semibold w-[300px]">Chipset</TableHead>
            <TableHead class="font-semibold w-[500px]">Name</TableHead>
            <TableHead class="font-semibold w-[500px]">System ID</TableHead>
           <!-- <TableHead class="font-semibold w-[500px]">Remark</TableHead> -->
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="5" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <template v-else>
            <TableRow v-for="model in RSStore.modelList" :key="model.id" class="cursor-pointer hover:bg-accent/50"
              @click="openUpdateDialog(model)">
              <TableCell class="px-5">{{ model.id }}</TableCell>
              <TableCell>{{RSStore.chipsetList.find(c => c.id === model.chipset_id)?.name || model.chipset_id}}
              </TableCell>
              <TableCell class="font-medium text-foreground">{{ model.name }}</TableCell>
              <TableCell>{{ model.sysid }}</TableCell>
              <!--  <TableCell class="text-muted-foreground">{{ model.remark }}</TableCell> -->
            </TableRow>
            <TableRow v-for="i in (UserStore.itemPerPage - RSStore.modelList.length)" :key="'empty-' + i">
              <TableCell colspan="5">&nbsp;</TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>
    <!-- 페이지네이션 -->
    <div class="mt-4 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalpage * UserStore.itemPerPage" :items-per-page="UserStore.itemPerPage"
        :sibling-count="UserStore.siblingCount" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>
      </Pagination>
    </div>
    <!-- 추가/수정 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {{ isUpdateMode ? "모델 수정" : "모델 추가" }}
          </DialogTitle>
          <DialogDescription>
            {{ isUpdateMode ? "모델 정보를 수정하세요" : "새로운 모델 정보를 입력하세요" }}
          </DialogDescription>
        </DialogHeader>
        <div class="grid gap-4 py-4 text-foreground">
          <div class="grid gap-2">
            <Label for="name">Name</Label>
            <Input id="name" v-model="formData.name" placeholder="모델 이름 입력" maxlength="32" />
          </div>
          <div class="grid gap-2">
            <Label for="chipset">Chipset</Label>
            <Select v-model="formData.chipset_id">
              <SelectTrigger id="chipset">
                <SelectValue placeholder="칩셋 선택" max-length="32" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id">
                  {{ chipset.name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label for="systemId">System ID</Label>
            <Input id="systemId" v-model="formData.sysid" placeholder="시스템 ID 입력" maxlength="8" />
          </div>
          <div class="grid gap-2">
            <Label for="remark">Remark</Label>
            <Input id="remark" placeholder="비고 입력" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="cancelDialog">Cancel</Button>
          <Button v-if="isUpdateMode" variant="destructive" @click="handleDelete" :disabled="!UserStore.modelDelete">
            Delete
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <div class="inline-block cursor-not-allowed">
                  <Button @click="isUpdateMode ? handleUpdate() : handleAdd()"
                    :disabled="isFormInvalid || (!UserStore.modelEdit && isUpdateMode)" :class="{ 'pointer-events-none': isFormInvalid }">
                    {{ isUpdateMode ? "Update" : "Add" }}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p v-if="isFormInvalid">name, chipset, System ID를 입력해주세요.</p>
                <p v-else>모델 정보를 저장합니다.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>